import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import * as LucideIcons from "lucide-react";
import * as FontAwesome from "react-icons/fa";
import * as MaterialDesign from "react-icons/md";
import * as Bootstrap from "react-icons/bs";
import * as Feather from "react-icons/fi";
import { Search, ChevronDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface IconPickerProps {
  value?: string;
  onChange: (iconName: string) => void;
  label?: string;
  iconLibrary?: string;
  onLibraryChange?: (library: string) => void;
}

// Bibliotecas disponíveis com suas respectivas coleções de ícones
const ICON_LIBRARIES = {
  "lucide-react": {
    name: "Lucide React",
    icons: LucideIcons,
    iconList: [
      "Home",
      "Building",
      "Building2",
      "Lightbulb",
      "Trash2",
      "Droplet",
      "TreePine",
      "Trees",
      "Car",
      "Bus",
      "Bike",
      "Construction",
      "Hammer",
      "Wrench",
      "ShoppingCart",
      "Package",
      "AlertTriangle",
      "Shield",
      "Heart",
      "Users",
      "User",
      "Phone",
      "Mail",
      "MapPin",
      "Map",
      "Navigation",
      "Compass",
      "Flag",
      "Camera",
      "Image",
      "File",
      "FileText",
      "Folder",
      "FolderOpen",
      "Archive",
      "Bookmark",
      "Tag",
      "Tags",
      "Star",
      "Settings",
      "Tool",
      "Zap",
      "Activity",
      "TrendingUp",
      "BarChart",
      "PieChart",
      "DollarSign",
      "CreditCard",
      "Wifi",
      "Radio",
      "Tv",
      "Monitor",
      "Smartphone",
      "Tablet",
      "Watch",
      "Clock",
      "Calendar",
      "Bell",
      "Volume2",
      "Sun",
      "Moon",
      "Cloud",
      "CloudRain",
      "Snowflake",
      "Wind",
      "Flame",
      "Droplets",
      "Leaf",
      "Flower",
      "Sprout",
      "Recycle",
      "Trash",
      "ShoppingBag",
      "Gift",
      "Award",
      "Target",
      "CheckCircle",
      "XCircle",
      "Info",
      "HelpCircle",
      "AlertCircle",
      "MessageCircle",
      "MessageSquare",
      "Send",
      "Share2",
      "Download",
      "Upload",
      "Link",
      "Lock",
      "Unlock",
      "Key",
      "Eye",
      "EyeOff",
      "Search",
      "Filter",
      "Sliders",
      "Grid",
      "List",
      "Columns",
      "Square",
      "Circle",
      "Triangle",
      "Hexagon",
      "Percent",
      "Hash",
      "AtSign",
      "Code",
      "Terminal",
      "Database",
      "Server",
      "Globe",
      "WifiOff",
      "Bluetooth",
      "Cast",
      "Printer",
      "Save",
      "Edit",
      "Copy",
      "Clipboard",
      "Scissors",
      "PlusCircle",
      "MinusCircle",
      "PlayCircle",
      "PauseCircle",
      "StopCircle",
      "SkipForward",
      "SkipBack",
      "FastForward",
      "Rewind",
      "RotateCw",
      "RotateCcw",
      "RefreshCw",
      "Maximize",
      "Minimize",
      "ZoomIn",
      "ZoomOut",
      "Move",
      "Crosshair",
      "Navigation2",
    ],
  },
  "font-awesome": {
    name: "Font Awesome",
    icons: FontAwesome,
    iconList: [
      "FaHome",
      "FaUser",
      "FaUsers",
      "FaCog",
      "FaHeart",
      "FaStar",
      "FaTrash",
      "FaEdit",
      "FaSave",
      "FaSearch",
      "FaPhone",
      "FaEnvelope",
      "FaShoppingCart",
      "FaBox",
      "FaCar",
      "FaBus",
      "FaBicycle",
      "FaPlane",
      "FaBuilding",
      "FaIndustry",
      "FaHospital",
      "FaSchool",
      "FaStore",
      "FaTree",
      "FaLeaf",
      "FaSeedling",
      "FaRecycle",
      "FaLightbulb",
      "FaBolt",
      "FaFire",
      "FaWater",
      "FaSnowflake",
      "FaCloudRain",
      "FaSun",
      "FaMoon",
      "FaCamera",
      "FaImage",
      "FaFilm",
      "FaMusic",
      "FaVideo",
      "FaHeadphones",
      "FaMicrophone",
      "FaVolumeUp",
      "FaBell",
      "FaCalendar",
      "FaClock",
      "FaMapMarker",
      "FaMap",
      "FaCompass",
      "FaFlag",
      "FaBookmark",
      "FaTag",
      "FaTags",
      "FaArchive",
      "FaFolder",
      "FaFile",
      "FaFileAlt",
      "FaDatabase",
      "FaServer",
      "FaDesktop",
      "FaLaptop",
      "FaMobile",
      "FaTablet",
      "FaKeyboard",
      "FaMouse",
      "FaPrint",
      "FaWifi",
      "FaBluetooth",
      "FaBatteryFull",
      "FaPlug",
      "FaShoppingBag",
      "FaGift",
      "FaCreditCard",
      "FaMoneyBill",
      "FaChartBar",
      "FaChartLine",
      "FaChartPie",
      "FaPercent",
      "FaHashtag",
      "FaAt",
      "FaLink",
      "FaLock",
      "FaUnlock",
      "FaKey",
      "FaEye",
      "FaEyeSlash",
      "FaCheckCircle",
      "FaTimesCircle",
      "FaExclamationTriangle",
      "FaInfoCircle",
      "FaQuestionCircle",
      "FaComment",
      "FaComments",
      "FaPaperPlane",
      "FaShare",
      "FaDownload",
      "FaUpload",
      "FaCode",
      "FaTerminal",
      "FaHammer",
      "FaWrench",
      "FaCogs",
      "FaTools",
      "FaShield",
      "FaAward",
      "FaTrophy",
      "FaMedal",
      "FaGraduationCap",
    ],
  },
  "material-design": {
    name: "Material Design",
    icons: MaterialDesign,
    iconList: [
      "MdHome",
      "MdPerson",
      "MdPeople",
      "MdSettings",
      "MdFavorite",
      "MdStar",
      "MdDelete",
      "MdEdit",
      "MdSave",
      "MdSearch",
      "MdPhone",
      "MdEmail",
      "MdShoppingCart",
      "MdInventory",
      "MdDirectionsCar",
      "MdDirectionsBus",
      "MdDirectionsBike",
      "MdBusiness",
      "MdFactory",
      "MdLocalHospital",
      "MdSchool",
      "MdStore",
      "MdPark",
      "MdNature",
      "MdLightbulb",
      "MdFlash",
      "MdLocalFireDepartment",
      "MdWaterDrop",
      "MdAcUnit",
      "MdCloud",
      "MdWbSunny",
      "MdDarkMode",
      "MdCamera",
      "MdImage",
      "MdMovie",
      "MdMusicNote",
      "MdVideocam",
      "MdHeadphones",
      "MdMic",
      "MdVolumeUp",
      "MdNotifications",
      "MdCalendarToday",
      "MdAccessTime",
      "MdLocationOn",
      "MdMap",
      "MdExplore",
      "MdFlag",
      "MdBookmark",
      "MdLabel",
      "MdArchive",
      "MdFolder",
      "MdInsertDriveFile",
      "MdDescription",
      "MdStorage",
      "MdDns",
      "MdComputer",
      "MdLaptop",
      "MdPhoneAndroid",
      "MdTablet",
      "MdKeyboard",
      "MdMouse",
      "MdPrint",
      "MdWifi",
      "MdBluetooth",
      "MdBattery",
      "MdPower",
      "MdShoppingBag",
      "MdCardGiftcard",
      "MdCreditCard",
      "MdAttachMoney",
      "MdBarChart",
      "MdShowChart",
      "MdPieChart",
      "MdPercent",
      "MdTag",
      "MdAlternateEmail",
      "MdLink",
      "MdLock",
      "MdLockOpen",
      "MdVpnKey",
      "MdVisibility",
      "MdVisibilityOff",
      "MdCheckCircle",
      "MdCancel",
      "MdWarning",
      "MdInfo",
      "MdHelp",
      "MdChat",
      "MdSend",
      "MdShare",
      "MdDownload",
      "MdUpload",
      "MdCode",
      "MdBuild",
      "MdConstruction",
      "MdSecurity",
      "MdEmojiEvents",
    ],
  },
  bootstrap: {
    name: "Bootstrap Icons",
    icons: Bootstrap,
    iconList: [
      "BsHouseDoor",
      "BsPerson",
      "BsPeople",
      "BsGear",
      "BsHeart",
      "BsStar",
      "BsTrash",
      "BsPencil",
      "BsDownload",
      "BsSearch",
      "BsPhone",
      "BsEnvelope",
      "BsCart",
      "BsBox",
      "BsCar",
      "BsBus",
      "BsBicycle",
      "BsBuilding",
      "BsTree",
      "BsLightbulb",
      "BsLightning",
      "BsFire",
      "BsDroplet",
      "BsSnow",
      "BsCloud",
      "BsSun",
      "BsMoon",
      "BsCamera",
      "BsImage",
      "BsFileMusic",
      "BsMic",
      "BsVolumeUp",
      "BsBell",
      "BsCalendar",
      "BsClock",
      "BsGeoAlt",
      "BsMap",
      "BsCompass",
      "BsFlag",
      "BsBookmark",
      "BsTag",
      "BsArchive",
      "BsFolder",
      "BsFileText",
      "BsDatabase",
      "BsServer",
      "BsDisplay",
      "BsLaptop",
      "BsPhone",
      "BsTablet",
      "BsKeyboard",
      "BsPrinter",
      "BsWifi",
      "BsBluetooth",
      "BsBatteryFull",
      "BsPlug",
      "BsBag",
      "BsGift",
      "BsCreditCard",
      "BsCash",
      "BsGraphUp",
      "BsPieChart",
      "BsPercent",
      "BsHash",
      "BsAt",
      "BsLink",
      "BsLock",
      "BsUnlock",
      "BsKey",
      "BsEye",
      "BsEyeSlash",
      "BsCheckCircle",
      "BsXCircle",
      "BsExclamationTriangle",
      "BsInfoCircle",
      "BsQuestionCircle",
      "BsChat",
      "BsSend",
      "BsShare",
      "BsCloudUpload",
      "BsCode",
      "BsTerminal",
      "BsHammer",
      "BsWrench",
      "BsTools",
      "BsShield",
      "BsTrophy",
      "BsAward",
      "BsMortarboard",
    ],
  },
  feather: {
    name: "Feather Icons",
    icons: Feather,
    iconList: [
      "FiHome",
      "FiUser",
      "FiUsers",
      "FiSettings",
      "FiHeart",
      "FiStar",
      "FiTrash",
      "FiEdit",
      "FiSave",
      "FiSearch",
      "FiPhone",
      "FiMail",
      "FiShoppingCart",
      "FiPackage",
      "FiTruck",
      "FiActivity",
      "FiAirplay",
      "FiAlertCircle",
      "FiAlertTriangle",
      "FiArchive",
      "FiAward",
      "FiBarChart",
      "FiBell",
      "FiBookmark",
      "FiBox",
      "FiBriefcase",
      "FiCalendar",
      "FiCamera",
      "FiCast",
      "FiCheck",
      "FiCheckCircle",
      "FiChrome",
      "FiCircle",
      "FiClipboard",
      "FiClock",
      "FiCloud",
      "FiCloudDrizzle",
      "FiCloudLightning",
      "FiCloudRain",
      "FiCloudSnow",
      "FiCode",
      "FiCommand",
      "FiCompass",
      "FiCpu",
      "FiCreditCard",
      "FiDatabase",
      "FiDelete",
      "FiDisc",
      "FiDollarSign",
      "FiDownload",
      "FiDroplet",
      "FiEye",
      "FiEyeOff",
      "FiFile",
      "FiFileText",
      "FiFilm",
      "FiFilter",
      "FiFlag",
      "FiFolder",
      "FiGift",
      "FiGlobe",
      "FiGrid",
      "FiHash",
      "FiHeadphones",
      "FiHelpCircle",
      "FiImage",
      "FiInfo",
      "FiKey",
      "FiLayers",
      "FiLayout",
      "FiLink",
      "FiList",
      "FiLock",
      "FiMap",
      "FiMapPin",
      "FiMessageCircle",
      "FiMessageSquare",
      "FiMic",
      "FiMonitor",
      "FiMoon",
      "FiMusic",
      "FiNavigation",
      "FiPercent",
      "FiPhone",
      "FiPieChart",
      "FiPower",
      "FiPrinter",
      "FiRadio",
      "FiServer",
      "FiShare",
      "FiShield",
      "FiShoppingBag",
      "FiSliders",
      "FiSmartphone",
      "FiSun",
      "FiTablet",
      "FiTag",
      "FiTarget",
      "FiTerminal",
      "FiThumbsUp",
      "FiTool",
      "FiTrendingUp",
      "FiTv",
      "FiUpload",
      "FiVideo",
      "FiVolume",
      "FiWifi",
      "FiXCircle",
      "FiZap",
    ],
  },
};

export function IconPicker({
  value,
  onChange,
  label,
  iconLibrary = "lucide-react",
  onLibraryChange,
}: IconPickerProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLibrary, setSelectedLibrary] = useState(iconLibrary);

  const currentLibrary =
    ICON_LIBRARIES[selectedLibrary as keyof typeof ICON_LIBRARIES] ||
    ICON_LIBRARIES["lucide-react"];

  const filteredIcons = currentLibrary.iconList.filter((iconName) =>
    iconName.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleLibraryChange = (library: string) => {
    setSelectedLibrary(library);
    if (onLibraryChange) {
      onLibraryChange(library);
    }
  };

  const renderIcon = (iconName: string, size: number = 20) => {
    try {
      const IconComponent = (currentLibrary.icons as any)[iconName];
      if (!IconComponent) {
        console.warn(`Icon ${iconName} not found in ${selectedLibrary}`);
        return null;
      }
      return <IconComponent size={size} />;
    } catch (error) {
      console.error(`Error rendering icon ${iconName}:`, error);
      return null;
    }
  };

  const getIconName = () => {
    if (!value) return "Selecione um ícone";
    return value;
  };

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-between cursor-pointer"
          >
            <div className="flex items-center gap-2">
              {value ? (
                <>
                  {renderIcon(value)}
                  <span>{getIconName()}</span>
                </>
              ) : (
                <span className="text-muted-foreground">
                  Selecione um ícone
                </span>
              )}
            </div>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[400px] p-3"
          align="start"
          side="bottom"
          sideOffset={5}
          onWheel={(e) => e.stopPropagation()}
        >
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-xs font-medium">
                Biblioteca de Ícones
              </Label>
              <Select
                value={selectedLibrary}
                onValueChange={handleLibraryChange}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ICON_LIBRARIES).map(([key, lib]) => (
                    <SelectItem key={key} value={key}>
                      {lib.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar ícone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <div
              className="h-[240px] overflow-y-auto pr-2 scroll-smooth"
              style={{ overscrollBehavior: "contain" }}
            >
              <div className="grid grid-cols-6 gap-2">
                {filteredIcons.map((iconName) => {
                  const IconComponent = (currentLibrary.icons as any)[iconName];
                  if (!IconComponent) return null;

                  return (
                    <button
                      key={iconName}
                      type="button"
                      onClick={() => {
                        onChange(iconName);
                        setIsOpen(false);
                        setSearchTerm("");
                      }}
                      className={cn(
                        "flex items-center justify-center p-3 rounded-md border hover:bg-primary/10 hover:border-primary transition-colors cursor-pointer",
                        value === iconName &&
                          "bg-primary/10 border-primary ring-2 ring-primary/20",
                      )}
                      title={iconName}
                    >
                      <IconComponent size={20} />
                    </button>
                  );
                })}
              </div>

              {filteredIcons.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">Nenhum ícone encontrado</p>
                </div>
              )}
            </div>

            <div className="pt-3 border-t flex justify-between items-center">
              <span className="text-xs text-muted-foreground">
                {filteredIcons.length} ícones disponíveis
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsOpen(false);
                  setSearchTerm("");
                }}
                className="cursor-pointer"
              >
                Fechar
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
